/**
 * Training CSV Import Engine
 * 
 * Parses CSV, validates rows, upserts artifacts, creates offerings (with dedupe),
 * and auto-maps capabilities. Supports dry-run mode.
 */

import Papa from 'papaparse';
import { fromZonedTime } from 'date-fns-tz';
import { supabase } from '@/integrations/supabase/client';
import { inferCapabilities } from './capabilityAutoMap';

// ─── Types ──────────────────────────────────────────────────────────

export interface TrainingCsvRow {
  title: string;
  provider_name: string;
  instructor_name: string;
  description: string;
  access_url: string;
  artifact_type: string;
  delivery_format: string;
  access_type: string;
  confidence_level: string;
  skill_depth: string;
  estimated_time_minutes: string;
  session_code: string;
  sponsor: string;
  start_date: string;
  start_time: string;
  end_time: string;
  timezone: string;
  delivery_type: string;
  city: string;
  state: string;
  venue_name: string;
  registration_url: string;
}

export interface ImportOptions {
  defaultProviderName: string;
  defaultTimezone: string;
  defaultCity: string;
  defaultState: string;
  defaultVenueName: string;
  dryRun: boolean;
}

export interface ImportError {
  row: number;
  reason: string;
}

export interface ImportSummary {
  created_artifacts: number;
  skipped_artifacts: number;
  created_offerings: number;
  skipped_offerings: number;
  created_capability_maps: number;
  skipped_rows: number;
  errors: ImportError[];
}

// ─── Provider Alias Normalization ───────────────────────────────────

const PROVIDER_ALIASES: Record<string, string> = {
  'vision': 'VISION Hi-Tech Training',
  'vision hi-tech': 'VISION Hi-Tech Training',
  'vision training': 'VISION Hi-Tech Training',
  'vision hi-tech training': 'VISION Hi-Tech Training',
};

function normalizeProviderName(raw: string): string {
  const trimmed = raw.trim().replace(/\s+/g, ' ');
  const key = trimmed.toLowerCase();
  return PROVIDER_ALIASES[key] || trimmed;
}

// ─── Skill Depth Normalization ──────────────────────────────────────

function normalizeSkillDepth(raw: string): string {
  const v = (raw || '').toLowerCase().trim();
  if (!v) return 'applied';
  if (['foundation', 'beginner', 'intro', 'introductory', 'basic'].includes(v)) return 'foundation';
  if (['applied', 'intermediate', 'mid', 'hands-on'].includes(v)) return 'applied';
  if (['advanced', 'expert', 'senior'].includes(v)) return 'advanced';
  if (['specialist', 'master', 'specialized'].includes(v)) return 'specialist';
  return 'applied'; // Safe default for unknown values
}

// ─── Delivery Type Normalization ────────────────────────────────────

function normalizeDeliveryType(raw: string): string {
  const v = (raw || '').toLowerCase().trim();
  if (!v) return 'in_person';
  if (['online', 'virtual', 'remote', 'virtual_live'].includes(v)) return 'virtual_live';
  if (['in_person', 'in-person', 'onsite', 'on-site'].includes(v)) return 'in_person';
  if (v === 'hybrid') return 'hybrid';
  return 'in_person'; // Safe default
}

// ─── Date/Time Normalization ────────────────────────────────────────

/**
 * Normalize date strings to YYYY-MM-DD format.
 * Handles: "3/4/2026", "03/04/2026", "2026-03-04", "March 4, 2026"
 */
function normalizeDate(raw: string): string | null {
  if (!raw) return null;
  const s = raw.trim();
  
  // Already ISO: YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  
  // MM/DD/YYYY or M/D/YYYY
  const slashMatch = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (slashMatch) {
    const [, m, d, y] = slashMatch;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  
  // Try native Date parsing as fallback (e.g. "March 4, 2026")
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) {
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, '0');
    const d = String(parsed.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  
  return null;
}

/**
 * Normalize time strings to HH:MM (24h) format.
 * Handles: "8:00", "08:00", "8:00 AM", "1:30 PM", "13:30"
 */
function normalizeTime(raw: string): string | null {
  if (!raw) return null;
  const s = raw.trim();
  
  // Already 24h: HH:MM
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  
  // H:MM (single digit hour, 24h)
  if (/^\d:\d{2}$/.test(s)) return `0${s}`;
  
  // 12h with AM/PM
  const ampmMatch = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm|AM|PM)$/i);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1], 10);
    const min = ampmMatch[2];
    const period = ampmMatch[3].toLowerCase();
    if (period === 'pm' && h < 12) h += 12;
    if (period === 'am' && h === 12) h = 0;
    return `${String(h).padStart(2, '0')}:${min}`;
  }
  
  // Just digits like "800" or "1300"
  if (/^\d{3,4}$/.test(s)) {
    const padded = s.padStart(4, '0');
    return `${padded.slice(0, 2)}:${padded.slice(2)}`;
  }
  
  return null;
}

// ─── CSV Template ───────────────────────────────────────────────────

const CSV_HEADERS = [
  'title', 'provider_name', 'instructor_name', 'description', 'access_url',
  'artifact_type', 'delivery_format', 'access_type', 'confidence_level',
  'skill_depth', 'estimated_time_minutes', 'session_code', 'sponsor',
  'start_date', 'start_time', 'end_time', 'timezone', 'delivery_type',
  'city', 'state', 'venue_name', 'registration_url',
];

const EXAMPLE_ROW = [
  'Advanced Electrical Diagnostics', 'VISION Hi-Tech Training', 'John Smith',
  'Circuit testing and voltage drop analysis', '', 'live_seminar', 'live',
  'paid', 'optibay_verified', 'applied', '480', 'V25-101', 'AESwave',
  '2025-03-10', '08:00', '17:00', 'America/Chicago', 'in_person',
  'Overland Park', 'KS', 'Overland Park Convention Center',
  'https://visionkc.com/register/v25-101',
];

export function generateTemplateCsv(): string {
  return CSV_HEADERS.join(',') + '\n' + EXAMPLE_ROW.join(',');
}

// ─── CSV Parsing ────────────────────────────────────────────────────

export function parseTrainingCsv(
  file: File
): Promise<{ rows: TrainingCsvRow[]; errors: ImportError[] }> {
  return new Promise((resolve) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h: string) => h.trim().toLowerCase().replace(/\s+/g, '_'),
      complete(results) {
        const rows: TrainingCsvRow[] = [];
        const errors: ImportError[] = [];

        results.data.forEach((raw, idx) => {
          const rowNum = idx + 2; // 1-indexed + header
          const row: TrainingCsvRow = {
            title: (raw.title || '').trim(),
            provider_name: (raw.provider_name || '').trim(),
            instructor_name: (raw.instructor_name || '').trim(),
            description: (raw.description || '').trim(),
            access_url: (raw.access_url || '').trim(),
            artifact_type: (raw.artifact_type || '').trim(),
            delivery_format: (raw.delivery_format || '').trim(),
            access_type: (raw.access_type || '').trim(),
            confidence_level: (raw.confidence_level || '').trim(),
            skill_depth: (raw.skill_depth || '').trim(),
            estimated_time_minutes: (raw.estimated_time_minutes || '').trim(),
            session_code: (raw.session_code || '').trim(),
            sponsor: (raw.sponsor || '').trim(),
            start_date: (raw.start_date || '').trim(),
            start_time: (raw.start_time || '').trim(),
            end_time: (raw.end_time || '').trim(),
            timezone: (raw.timezone || '').trim(),
            delivery_type: (raw.delivery_type || '').trim(),
            city: (raw.city || '').trim(),
            state: (raw.state || '').trim(),
            venue_name: (raw.venue_name || '').trim(),
            registration_url: (raw.registration_url || '').trim(),
          };

          // Parse-time validation
          if (!row.title) {
            errors.push({ row: rowNum, reason: 'Missing required field: title' });
            return;
          }
          if (!row.registration_url) {
            errors.push({ row: rowNum, reason: 'Missing required field: registration_url' });
            return;
          }
          if (!row.start_date || !row.start_time || !row.end_time) {
            errors.push({ row: rowNum, reason: 'Missing required date/time fields (start_date, start_time, end_time)' });
            return;
          }
          if (!row.delivery_type) {
            errors.push({ row: rowNum, reason: 'Missing required field: delivery_type' });
            return;
          }

          rows.push(row);
        });

        resolve({ rows, errors });
      },
      error(err: Error) {
        resolve({ rows: [], errors: [{ row: 0, reason: `CSV parse error: ${err.message}` }] });
      },
    });
  });
}

// ─── Import Engine ──────────────────────────────────────────────────

export async function importTrainingCsv(
  rows: TrainingCsvRow[],
  options: ImportOptions
): Promise<ImportSummary> {
  const summary: ImportSummary = {
    created_artifacts: 0,
    skipped_artifacts: 0,
    created_offerings: 0,
    skipped_offerings: 0,
    created_capability_maps: 0,
    skipped_rows: 0,
    errors: [],
  };

  if (rows.length === 0) return summary;

  // ── 1. Apply defaults & normalize ─────────────────────────────────
  const processedRows = rows.map((row, idx) => {
    const providerName = normalizeProviderName(row.provider_name || options.defaultProviderName);
    const timezone = row.timezone || options.defaultTimezone;
    const deliveryType = normalizeDeliveryType(row.delivery_type);
    const city = row.city || (deliveryType !== 'virtual_live' ? options.defaultCity : '');
    const state = row.state || (deliveryType !== 'virtual_live' ? options.defaultState : '');
    const venueName = row.venue_name || (deliveryType !== 'virtual_live' ? options.defaultVenueName : '');
    const accessUrl = row.access_url || row.registration_url;

    return {
      ...row,
      _rowNum: idx + 2,
      provider_name: providerName,
      timezone,
      delivery_type: deliveryType,
      city,
      state,
      venue_name: venueName,
      access_url: accessUrl,
    };
  });

  // ── 2. Resolve providers (fail-fast) ──────────────────────────────
  const { data: allProviders, error: providerError } = await supabase
    .from('training_providers')
    .select('id, name, display_name')
    .eq('is_active', true);

  if (providerError) {
    summary.errors.push({ row: 0, reason: `Failed to fetch providers: ${providerError.message}` });
    return summary;
  }

  const providerMap = new Map<string, string>();
  for (const p of allProviders || []) {
    // Index by both name (slug) and display_name so either can match
    providerMap.set(p.name.toLowerCase().trim(), p.id);
    if (p.display_name) {
      providerMap.set(p.display_name.toLowerCase().trim(), p.id);
    }
  }

  // Validate all rows have resolvable providers
  const validRows: typeof processedRows = [];
  for (const row of processedRows) {
    const normalizedName = row.provider_name.toLowerCase().trim();
    if (!providerMap.has(normalizedName)) {
      summary.errors.push({
        row: row._rowNum,
        reason: `Provider not found: "${row.provider_name}". Add it in the Providers tab first.`,
      });
      summary.skipped_rows++;
      continue;
    }

    // Validate in_person/hybrid need city+state
    if (['in_person', 'hybrid'].includes(row.delivery_type) && (!row.city || !row.state)) {
      summary.errors.push({
        row: row._rowNum,
        reason: `delivery_type "${row.delivery_type}" requires city and state`,
      });
      summary.skipped_rows++;
      continue;
    }

    validRows.push(row);
  }

  if (validRows.length === 0) return summary;
  if (options.dryRun) {
    // Estimate counts without DB writes
    return estimateDryRun(validRows, providerMap, summary);
  }

  // ── 3. Dedupe artifacts by provider_name + title ──────────────────
  const uniqueArtifacts = new Map<string, typeof validRows[0]>();
  for (const row of validRows) {
    const key = `${row.provider_name.toLowerCase()}||${row.title.toLowerCase()}`;
    if (!uniqueArtifacts.has(key)) {
      uniqueArtifacts.set(key, row);
    }
  }

  // Fetch existing artifacts by provider_name + title (chunked)
  const artifactKeys = Array.from(uniqueArtifacts.keys());
  const existingArtifactMap = new Map<string, string>(); // key -> id

  const titles = Array.from(uniqueArtifacts.values()).map(r => r.title);
  for (let i = 0; i < titles.length; i += 50) {
    const chunk = titles.slice(i, i + 50);
    const { data: existing } = await supabase
      .from('training_artifacts')
      .select('id, title, provider_name')
      .in('title', chunk);

    for (const art of existing || []) {
      const key = `${art.provider_name.toLowerCase()}||${art.title.toLowerCase()}`;
      existingArtifactMap.set(key, art.id);
    }
  }

  // Insert missing artifacts
  const artifactsToInsert: Array<Record<string, unknown>> = [];
  for (const [key, row] of uniqueArtifacts.entries()) {
    if (existingArtifactMap.has(key)) {
      summary.skipped_artifacts++;
      continue;
    }
    const providerId = providerMap.get(row.provider_name.toLowerCase().trim());
    artifactsToInsert.push({
      title: row.title,
      provider_name: row.provider_name,
      provider_id: providerId || null,
      instructor_name: row.instructor_name || null,
      access_url: row.access_url || row.registration_url,
      artifact_type: row.artifact_type || 'live_seminar',
      delivery_format: row.delivery_format || 'live',
      access_type: row.access_type || 'paid',
      confidence_level: row.confidence_level || 'optibay_verified',
      skill_depth: normalizeSkillDepth(row.skill_depth),
      teaching_styles: null,
      original_summary_short: row.description || null,
      estimated_time_minutes: row.estimated_time_minutes ? parseInt(row.estimated_time_minutes, 10) || null : null,
    });
  }

  if (artifactsToInsert.length > 0) {
    // Insert in batches of 50
    for (let i = 0; i < artifactsToInsert.length; i += 50) {
      const batch = artifactsToInsert.slice(i, i + 50);
      const { data: inserted, error: insertErr } = await supabase
        .from('training_artifacts')
        .insert(batch as any)
        .select('id, title, provider_name');

      if (insertErr) {
        summary.errors.push({ row: 0, reason: `Artifact insert failed: ${insertErr.message}` });
        continue;
      }

      for (const art of inserted || []) {
        const key = `${art.provider_name.toLowerCase()}||${art.title.toLowerCase()}`;
        existingArtifactMap.set(key, art.id);
        summary.created_artifacts++;
      }
    }
  }

  // ── 4. Pre-fetch existing offerings for dedupe ────────────────────
  const allArtifactIds = Array.from(new Set(
    validRows
      .map(r => existingArtifactMap.get(`${r.provider_name.toLowerCase()}||${r.title.toLowerCase()}`))
      .filter(Boolean) as string[]
  ));

  const existingOfferings: Array<{ artifact_id: string; start_datetime: string; registration_url: string }> = [];
  for (let i = 0; i < allArtifactIds.length; i += 50) {
    const chunk = allArtifactIds.slice(i, i + 50);
    const { data } = await supabase
      .from('training_offering_instances')
      .select('artifact_id, start_datetime, registration_url')
      .in('artifact_id', chunk);
    if (data) existingOfferings.push(...data);
  }

  // ── 5. Insert offerings ───────────────────────────────────────────
  for (const row of validRows) {
    const artifactKey = `${row.provider_name.toLowerCase()}||${row.title.toLowerCase()}`;
    const artifactId = existingArtifactMap.get(artifactKey);
    if (!artifactId) {
      summary.errors.push({ row: row._rowNum, reason: 'Could not resolve artifact ID after insert' });
      summary.skipped_rows++;
      continue;
    }

    const providerId = providerMap.get(row.provider_name.toLowerCase().trim());
    const timezone = row.timezone;

    // Convert datetime to UTC
    let startUTC: string;
    let endUTC: string;
    let expiresAt: string;
    try {
      const normalizedDate = normalizeDate(row.start_date);
      const normalizedStartTime = normalizeTime(row.start_time);
      const normalizedEndTime = normalizeTime(row.end_time);
      
      if (!normalizedDate || !normalizedStartTime || !normalizedEndTime) {
        summary.errors.push({ row: row._rowNum, reason: `Invalid date/time format: date="${row.start_date}" start="${row.start_time}" end="${row.end_time}"` });
        summary.skipped_rows++;
        continue;
      }
      
      const startLocal = `${normalizedDate}T${normalizedStartTime}:00`;
      const endLocal = `${normalizedDate}T${normalizedEndTime}:00`;
      startUTC = fromZonedTime(startLocal, timezone).toISOString();
      endUTC = fromZonedTime(endLocal, timezone).toISOString();
      expiresAt = new Date(new Date(endUTC).getTime() + 180 * 86400000).toISOString();
    } catch (e) {
      summary.errors.push({ row: row._rowNum, reason: `Date conversion failed: date="${row.start_date}" start="${row.start_time}" end="${row.end_time}" error=${e}` });
      summary.skipped_rows++;
      continue;
    }

    // Guard: end must be after start
    if (new Date(endUTC) <= new Date(startUTC)) {
      summary.errors.push({ row: row._rowNum, reason: 'end_time must be after start_time' });
      summary.skipped_rows++;
      continue;
    }

    // Dedupe: artifact_id + start_datetime + registration_url
    const isDuplicate = existingOfferings.some(
      o => o.artifact_id === artifactId &&
           o.start_datetime === startUTC &&
           o.registration_url === row.registration_url
    );

    if (isDuplicate) {
      summary.skipped_offerings++;
      continue;
    }

    // Lifecycle: if end_datetime is in the past, auto-set completed/inactive
    const isPastOffering = new Date(endUTC) < new Date();

    const { error: offeringErr } = await supabase
      .from('training_offering_instances')
      .insert({
        artifact_id: artifactId,
        provider_id: providerId || null,
        registration_url: row.registration_url,
        start_datetime: startUTC,
        end_datetime: endUTC,
        expires_at: expiresAt,
        timezone,
        delivery_type: normalizeDeliveryType(row.delivery_type),
        city: row.city || null,
        state: row.state || null,
        venue_name: row.venue_name || null,
        is_paid: true,
        status: isPastOffering ? 'completed' : 'upcoming',
        is_active: !isPastOffering,
      } as any);

    if (offeringErr) {
      summary.errors.push({ row: row._rowNum, reason: `Offering insert failed: ${offeringErr.message}` });
    } else {
      summary.created_offerings++;
    }
  }

  // ── 6. Auto-map capabilities per unique artifact ──────────────────
  const processedArtifactIds = new Set<string>();

  for (const [key, row] of uniqueArtifacts.entries()) {
    const artifactId = existingArtifactMap.get(key);
    if (!artifactId || processedArtifactIds.has(artifactId)) continue;
    processedArtifactIds.add(artifactId);

    const text = [row.title, row.description].filter(Boolean).join('\n');
    const capabilities = inferCapabilities(text);

    if (capabilities.length === 0) continue;

    for (const cap of capabilities) {
      // Check if mapping already exists
      const { data: existing } = await supabase
        .from('training_artifact_capability_map')
        .select('id')
        .eq('artifact_id', artifactId)
        .eq('capability_key', cap.capability_key)
        .maybeSingle();

      if (existing) continue; // Already mapped

      const { error: capErr } = await supabase
        .from('training_artifact_capability_map')
        .insert({
          artifact_id: artifactId,
          capability_key: cap.capability_key,
          weight: cap.weight,
          is_primary: cap.is_primary,
        });

      if (!capErr) {
        summary.created_capability_maps++;
      }
    }
  }

  return summary;
}

// ─── Dry Run Estimator ──────────────────────────────────────────────

function estimateDryRun(
  validRows: Array<TrainingCsvRow & { _rowNum: number }>,
  providerMap: Map<string, string>,
  summary: ImportSummary
): ImportSummary {
  const uniqueArtifacts = new Map<string, typeof validRows[0]>();
  for (const row of validRows) {
    const key = `${row.provider_name.toLowerCase()}||${row.title.toLowerCase()}`;
    if (!uniqueArtifacts.has(key)) {
      uniqueArtifacts.set(key, row);
    }
  }

  // Estimate artifacts (can't check existing without querying, so count unique)
  summary.created_artifacts = uniqueArtifacts.size;
  summary.created_offerings = validRows.length;

  // Estimate capability maps
  let capCount = 0;
  for (const [, row] of uniqueArtifacts.entries()) {
    const text = [row.title, row.description].filter(Boolean).join('\n');
    const caps = inferCapabilities(text);
    capCount += caps.length;
  }
  summary.created_capability_maps = capCount;

  return summary;
}

// ─── Error CSV Generator ────────────────────────────────────────────

export function generateErrorCsv(errors: ImportError[]): string {
  const lines = ['row,reason'];
  for (const e of errors) {
    // Escape double quotes in reason
    const safeReason = e.reason.replace(/"/g, '""');
    lines.push(`${e.row},"${safeReason}"`);
  }
  return lines.join('\n');
}
